import { useForms, useTranslations } from "./formQueries";
import { wordsOf, type Words } from "./formI18n";
import type { Form } from "./forms";
import { REFERENCE_KEY } from "../settings/remote";
import { useSettings } from "../settings/context";

/**
 * LE FORMULAIRE QU'UN ÉCRAN SERT, ET SES MOTS.
 *
 * ⚠️ TROIS ÉCRANS SERVENT UN FORMULAIRE DU BACK OFFICE — les informations, la société,
 * l'accompagnant — et les trois écrivaient les MÊMES quinze lignes : deux requêtes, la
 * résolution du réglage, puis `motsDe` avec sa langue de référence. Recopiées, elles
 * étaient libres de diverger, et c'est exactement ce qui était arrivé : la lecture du
 * réglage de l'accompagnant s'était mise à traiter la chaîne vide autrement que les
 * deux autres (voir `useFormChoisi`).
 *
 * Les LECTURES, elles, vivent dans `formQueries.ts` : le fournisseur des réglages en a
 * besoin lui aussi, et il ne peut pas importer ce qui l'importe.
 */

/**
 * LE FORMULAIRE DÉSIGNÉ PAR UN RÉGLAGE, avec un repli facultatif sur un second.
 *
 * ⚠️ UNE VALEUR VIDE VAUT « AUCUN CHOIX », ET PAS « CE CHOIX-CI ». Le panneau écrit la
 * CHAÎNE VIDE quand on remet un sélecteur sur « — » (`panel/Row.tsx`), et non
 * `undefined` : un `??` ne bascule donc jamais sur le repli. L'étape accompagnants en
 * dépendait — son catalogue promet « Vide : le formulaire de l'événement » — et le repli
 * ne se déclenchait pas dès qu'un formulaire avait été choisi puis retiré. La
 * conséquence n'était pas cosmétique : sans champs, l'étape ne pouvait plus lire de nom,
 * et l'ajout échouait sur « Indiquez au moins un nom » sans qu'aucun champ ne soit
 * affiché pour en donner un.
 */
export function useChosenForm(key: string, fallback?: string): Form | undefined {
  const { value } = useSettings();
  const forms = useForms();
  const chosen = settingId(value(key)) ?? (fallback ? settingId(value(fallback)) : undefined);
  return chosen === undefined
    ? undefined
    : forms.find((f) => String(f.id) === chosen);
}

/**
 * L'IDENTIFIANT PORTÉ PAR UN RÉGLAGE, ou `undefined` s'il n'en porte pas.
 *
 * Exportée pour son test : c'est la ligne où « rien de choisi » et « le choix numéro 0 »
 * doivent rester deux choses différentes, sous les trois formes que la base rend — un
 * nombre écrit par le panneau, une chaîne héritée de la V1, la chaîne vide d'un choix
 * effacé.
 */
export function settingId(raw: unknown): string | undefined {
  if (typeof raw === "number" && Number.isFinite(raw)) return String(raw);
  if (typeof raw === "string" && raw.trim() !== "") return raw.trim();
  return undefined;
}

/**
 * LES LIBELLÉS DE CE FORMULAIRE, DANS LA LANGUE LUE.
 *
 * ⚠️ FR EN DERNIER RECOURS, comme la V1. `languages` porte la clé de langue dans
 * `short` ; à défaut de FR actif, la langue par défaut de l'événement fait office —
 * mieux vaut un libellé dans une langue que le participant ne lit pas qu'un identifiant
 * technique à l'écran.
 */
export function useWords(form: Form | undefined): Words | undefined {
  const { scope, languages } = useSettings();
  const translations = useTranslations();

  if (!form) return undefined;
  return wordsOf(form, translations ?? {}, {
    current: scope.languageId,
    eventDefault: scope.defaultLanguageId,
    reference:
      languages.find((l) => l.short === REFERENCE_KEY)?.id ??
      scope.defaultLanguageId,
  });
}

/**
 * LES MOTS DE PLUSIEURS FORMULAIRES, par identifiant — pour un écran qui en sert un
 * nombre variable (le récapitulatif, avec ses instances de l'étape informations).
 * Un seul hook, quel que soit leur nombre : `useWords` dans une boucle changerait le
 * nombre de hooks d'un rendu à l'autre.
 */
export function useWordsOf(forms: ReadonlyArray<Form>): Map<number, Words> {
  const { scope, languages } = useSettings();
  const translations = useTranslations();
  const tongues = {
    current: scope.languageId,
    eventDefault: scope.defaultLanguageId,
    reference:
      languages.find((l) => l.short === REFERENCE_KEY)?.id ??
      scope.defaultLanguageId,
  };
  return new Map(forms.map((f) => [f.id, wordsOf(f, translations ?? {}, tongues)]));
}
